import _ from 'underscore';
import { parse } from '@bingads-webui/custom-column-parser-pegjs';

/**
 * @typedef {Object} SegmentDefinition
 * @property {string} segment internal segment name (for code)
 * @property {string} titleKey i18n key for segment name (for UI)
 */

/**
 * @typedef {Object} SegmentCategoryDefinition
 * @property {string} category internal segment category name (for code)
 * @property {string} titleKey i18n key for segment category name (for UI)
 * @property {SegmentDefinition[]} segments list of valid segment definitions
 */

/**
 * @typedef {Object} MetricDefinition
 * @property {string} metric internal metric name (for code)
 * @property {string} titleKey i18n key for metric name (for UI)
 */

/**
 * @typedef {Object} MetricCategoryDefinition
 * @property {string} titleKey i18n key for metric category name (for UI)
 * @property {MetricDefinition[]} metrics list of valid metric definitions
 */

/**
 * Configuration for CustomColumnParser constructor.
 *
 * @typedef {Object} CustomColumnParserConfig
 * @property {MetricCategoryDefinition[]} [metricCategories=[]] whitelist of valid metrics
 * @property {SegmentCategoryDefinition[]} [segmentCategories=[]] whitelist of valid segments
 */

/**
 * Swappable string-to-ast parsing engine.
 * Useful for quickly & easily swapping parse libraries/dependencies.
 *
 * @typedef {Object} CustomColumnParserEngine
 * @property {function} parse accepts string input, returns AST object
 */

/**
 * Parse and/or validate custom column expression strings.
 */
export class CustomColumnParser {
  /**
   * @param {CustomColumnParserConfig} [config] parser configuration
   * @param {CustomColumnParserEngine} [parserEngine] swappable engine for generating parse trees
   */
  constructor({ metricCategories = [], segmentCategories = [] } = {}, parserEngine = { parse }) {
    this.validMetrics = _.flatten(_.map(metricCategories, ({ metrics }) =>
      _.pluck(metrics, 'metric')));
    this.segmentCategories = segmentCategories;
    this.parserEngine = parserEngine;
  }

  isValidSegment(text = '') {
    const [category, selectionsText = ''] = text.split(':');
    const segmentConfig = _.findWhere(this.segmentCategories, { category });
    const validSegments = _.pluck(segmentConfig.segments, 'segment');

    if (!segmentConfig) {
      return false;
    }

    const selections = selectionsText.split(',');

    if (_.isEmpty(_.first(selections))) {
      return false;
    }

    return selections.length === _.intersection(selections, validSegments).length;
  }

  getColumnValidationErrors(root) {
    const errors = [];

    if (root.type === 'column') {
      if (!_.isEmpty(this.validMetrics) && !_.contains(this.validMetrics, root.name)) {
        errors.push({ cause: 'Invalid column name', offender: root.name });
      }

      if (!_.isEmpty(this.segmentCategories) && root.segments) {
        if (!this.isValidSegment(root.segments)) {
          errors.push({ cause: 'Invalid segment', offender: root.segments });
        }
      }
    }

    return errors
      .concat(root.left ? this.getColumnValidationErrors(root.left) : [])
      .concat(root.right ? this.getColumnValidationErrors(root.right) : []);
  }

  parse(expression) {
    try {
      const parseTree = this.parserEngine.parse(expression);
      const errors = this.getColumnValidationErrors(parseTree);

      if (!_.isEmpty(errors)) {
        return null;
      }

      return parseTree;
    } catch (e) {
      return null;
    }
  }

  isValid(expression) {
    try {
      return Boolean(this.parse(expression));
    } catch (e) {
      return false;
    }
  }
}
